import { sendAuthenticatedAsyncRequest } from "services/AsyncRequestService";
import { getLoggedInUser } from "services/AuthService";

/**
 * Evaluates feature flags via platform_backend
 * POST /feature-flags/{flagName}/evaluate (Statsig is evaluated on the BE).
 */
class PlatformFeatureFlagService {
  constructor() {
    this.user = { userID: null, email: null };
    this.initialized = false;
  }

  async initialize(user = {}) {
    this.user = {
      userID: user.userID ?? null,
      email: user.email ?? null,
    };
    this.initialized = true;
  }

  async clear() {
    this.initialized = false;
    this.user = { userID: null, email: null };
  }

  async ensureInitialized() {
    if (this.initialized) {
      return;
    }
    try {
      const user = getLoggedInUser();
      await this.initialize({ userID: user.uuid, email: user.email });
    } catch (error) {
      console.warn(
        "Feature flag initialization failed, evaluations will use defaults",
        error
      );
      throw error;
    }
  }

  async getFeatureFlagValue(key, defaultValue) {
    try {
      await this.ensureInitialized();
    } catch {
      return defaultValue;
    }

    try {
      const enabled = await this.evaluateFlag(key);
      return typeof enabled === "boolean" ? enabled : defaultValue;
    } catch (error) {
      console.warn(
        "Platform feature flag evaluation failed, using default value for",
        key,
        error
      );
      return defaultValue;
    }
  }

  async isFeatureEnabled(key, defaultValue = false) {
    try {
      const value = await this.getFeatureFlagValue(key, defaultValue);
      if (typeof value === "boolean") {
        return value;
      }
      return defaultValue;
    } catch (error) {
      console.error("Error checking feature flag value:", error);
      return defaultValue;
    }
  }

  evaluateFlag(flagName) {
    const body = {
      userId: this.user?.userID ?? null,
      email: this.user?.email ?? null,
    };

    return new Promise((resolve, reject) => {
      sendAuthenticatedAsyncRequest(
        `/feature-flags/${encodeURIComponent(flagName)}/evaluate`,
        "POST",
        body,
        (response) => {
          resolve(response?.data?.enabled);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }
}

const platformFeatureFlagService = new PlatformFeatureFlagService();

export default platformFeatureFlagService;
